import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { AUDIO_DEVICE_CHANGED, AUDIO_DEVICE_DISABLED, CLOSE_CLICKED, COUNTDOWN_COMPLETE, FINISH_CLICKED, ONCOMPLETE, SAVE_CLICKED, START_CLICKED, STARTOVER_CLICKED, VIDEO_DEVICE_CHANGED, VIDEO_DEVICE_DISABLED, DEVICE_REQUEST_ACCEPTED, MEDIA_RECORDER_INITIALIZED, VIDEO_OBJECT_GENERATED, ERROR_OCCURRED, DEVICES_FOUND, SCREEN_SHARING_ENABLED, SCREEN_SHARING_DISABLED, SCREEN_SHARING_ENDED, DEVICE_REQUEST_FAILED, PAUSE_CLICKED, CONTINUE_CLICKED, UPDATE_TIMER, SCREEN_SHARING_EXTRA_OPTIONS_SET, SCREEN_SHARING_TYPE_CHANGED, PICTURE_IN_PICTURE_ENABLED, PICTURE_IN_PICTURE_DISABLED, SHOW_PIP_MODAL, HIDE_PIP_MODAL, TOGGLE_CAMERA_EFFECT, SYSTEM_PERMISSION_ERROR, ERROR_CLEARED } from '../constants/ActionTypes';
import { PREVIEWSAVE, READY, RECORDING, SAVING, STARTING, LOADING, ERROR, FINISHED, PAUSED } from '../constants/CaptureStates';
import { FRAME_RATE } from '../constants/streamConfig';
import StreamMerger from '../core/StreamMerger';
import SoundMeter from '../core/SoundMeter';
export function getInitialState() {
  return {
    captureState: LOADING,
    videoSrc: '',
    videoBlob: '',
    msg: '',
    devices: {
      audioinput: [],
      videoinput: []
    },
    audioDeviceId: '',
    videoDeviceId: '',
    lastVideoDeviceId: '',
    showPipModal: false,
    requestAudioOnly: false,
    screenSharingActive: false,
    screenSharingType: 'none',
    pipEnabled: false,
    fileName: '',
    soundMeter: new SoundMeter(),
    mediaRecorder: void 0,
    streamMerger: new StreamMerger(FRAME_RATE),
    timeElapsed: 0,
    experimentalScreenShareOptions: {},
    disableInMemoryRecording: false,
    cameraEffects: [],
    noSystemPermissionDevices: new Set()
  };
}
export function reducer(state, action) {
  var _action$devices$video;
  switch (action.type) {
    case AUDIO_DEVICE_CHANGED:
      return _objectSpread(_objectSpread({}, state), {}, {
        audioDeviceId: action.id
      });
    case AUDIO_DEVICE_DISABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        audioDeviceId: null
      });
    case CLOSE_CLICKED:
      if ([READY, STARTING, RECORDING, PREVIEWSAVE].includes(state.captureState)) {
        return _objectSpread(_objectSpread({}, state), {}, {
          captureState: READY
        });
      } else {
        return state;
      }
    case COUNTDOWN_COMPLETE:
      if (state.captureState !== STARTING) {
        return state;
      }
      return _objectSpread(_objectSpread({}, state), {}, {
        captureState: RECORDING
      });
    case UPDATE_TIMER:
      return _objectSpread(_objectSpread({}, state), {}, {
        timeElapsed: action.time
      });
    case DEVICE_REQUEST_ACCEPTED:
      return _objectSpread(_objectSpread({}, state), {}, {
        captureState: READY
      });
    case DEVICE_REQUEST_FAILED:
      return _objectSpread(_objectSpread({}, state), {}, {
        captureState: READY
      });
    case DEVICES_FOUND:
      return _objectSpread(_objectSpread({}, state), {}, {
        devices: action.devices,
        audioDeviceId: action.selectedAudioId,
        videoDeviceId: action.selectedVideoId,
        requestAudioOnly: !(((_action$devices$video = action.devices.videoinput) === null || _action$devices$video === void 0 ? void 0 : _action$devices$video.length) > 0) || !action.selectedVideoId
      });
    case ERROR_OCCURRED:
      return _objectSpread(_objectSpread({}, state), {}, {
        msg: action.msg,
        captureState: action.critical ? ERROR : state.captureState
      });
    case ERROR_CLEARED:
      return _objectSpread(_objectSpread({}, state), {}, {
        msg: '',
        captureState: READY
      });
    case FINISH_CLICKED:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          fileName: action.fileName,
          captureState: PREVIEWSAVE
        });
      }
    case PAUSE_CLICKED:
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          captureState: PAUSED
        });
      }
    case CONTINUE_CLICKED:
      {
        if (state.captureState !== PAUSED) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          captureState: RECORDING
        });
      }
    case ONCOMPLETE:
      {
        if (![SAVING, FINISHED].includes(state.captureState)) {
          return state;
        }
        return _objectSpread(_objectSpread({}, state), {}, {
          soundMeter: state.soundMeter,
          captureState: FINISHED
        });
      }
    case MEDIA_RECORDER_INITIALIZED:
      return _objectSpread(_objectSpread({}, state), {}, {
        mediaRecorder: action.mr
      });
    case SAVE_CLICKED:
      if (state.captureState !== PREVIEWSAVE) {
        return state;
      }
      return _objectSpread(_objectSpread({}, state), {}, {
        fileName: action.fileName,
        captureState: SAVING
      });
    case START_CLICKED:
      if (state.captureState !== READY) {
        return state;
      }
      return _objectSpread(_objectSpread({}, state), {}, {
        captureState: STARTING
      });
    case STARTOVER_CLICKED:
      if (![RECORDING, PREVIEWSAVE, FINISHED].includes(state.captureState)) {
        return state;
      }
      return _objectSpread(_objectSpread({}, getInitialState()), {}, {
        devices: state.devices,
        audioDeviceId: state.audioDeviceId,
        videoDeviceId: state.videoDeviceId,
        requestAudioOnly: state.requestAudioOnly,
        captureState: state.captureState === PREVIEWSAVE ? LOADING : READY,
        experimentalScreenShareOptions: state.experimentalScreenShareOptions
      });
    case VIDEO_DEVICE_CHANGED:
      return _objectSpread(_objectSpread({}, state), {}, {
        requestAudioOnly: false,
        videoDeviceId: action.id || state.lastVideoDeviceId,
        showPipModal: false
      });
    case VIDEO_DEVICE_DISABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        requestAudioOnly: !state.screenSharingActive,
        videoDeviceId: null,
        lastVideoDeviceId: state.videoDeviceId !== null ? state.videoDeviceId : state.lastVideoDeviceId
      });
    case VIDEO_OBJECT_GENERATED:
      return _objectSpread(_objectSpread({}, state), {}, {
        videoSrc: action.src,
        videoBlob: action.blob
      });
    case SCREEN_SHARING_ENABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        screenSharingActive: true,
        requestAudioOnly: false
      });
    case SHOW_PIP_MODAL:
      return _objectSpread(_objectSpread({}, state), {}, {
        showPipModal: true
      });
    case HIDE_PIP_MODAL:
      return _objectSpread(_objectSpread({}, state), {}, {
        showPipModal: false
      });
    case SCREEN_SHARING_DISABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        screenSharingActive: false,
        requestAudioOnly: !state.videoDeviceId,
        screenSharingType: 'none'
      });
    case SCREEN_SHARING_TYPE_CHANGED:
      return _objectSpread(_objectSpread({}, state), {}, {
        screenSharingType: action.screenSharingType
      });
    case PICTURE_IN_PICTURE_ENABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        pipEnabled: true
      });
    case PICTURE_IN_PICTURE_DISABLED:
      return _objectSpread(_objectSpread({}, state), {}, {
        pipEnabled: false
      });
    case SCREEN_SHARING_ENDED:
      switch (state.captureState) {
        case PAUSED:
        case RECORDING:
          // we don't change screenSharingActive flag in this case
          // to preserve it till the end (startover or cleanup).
          // Logically end of recording should remember device configuration
          // although screenSharing is not longer active
          return _objectSpread(_objectSpread({}, state), {}, {
            fileName: action.fileName,
            captureState: PREVIEWSAVE
          });
        default:
          return _objectSpread(_objectSpread({}, state), {}, {
            screenSharingActive: false,
            requestAudioOnly: !state.videoDeviceId,
            screenSharingType: 'none'
          });
      }
    case SCREEN_SHARING_EXTRA_OPTIONS_SET:
      return _objectSpread(_objectSpread({}, state), {}, {
        experimentalScreenShareOptions: action.options
      });
    case TOGGLE_CAMERA_EFFECT:
      {
        const cameraEffects = state.cameraEffects;
        const effect = action.effect;
        const newCameraEffects = cameraEffects.includes(effect) ? cameraEffects.filter(eff => eff !== effect) : cameraEffects.concat(effect);
        return _objectSpread(_objectSpread({}, state), {}, {
          cameraEffects: newCameraEffects
        });
      }
    case SYSTEM_PERMISSION_ERROR:
      return _objectSpread(_objectSpread({}, state), {}, {
        noSystemPermissionDevices: new Set([...Array.from(state.noSystemPermissionDevices), action.device])
      });
    default:
      return state;
  }
}